import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMap } from 'react-map-gl';
import { useMapControl } from 'components/map/control-slot';
import { crossSectionArmedAtom, registerCrossSectionToolAtom } from './atoms';
import { CrossSectionArmButton } from './cross-section-arm-button';
import { CrossSectionMapLayer } from './cross-section-map-layer';

/**
 * Turns the Cross-Section tool on for a map. Mount it as a child of `<Map>`:
 *
 *     <Map ...>
 *       <CrossSectionMapTool />
 *     </Map>
 *
 * That one line is the whole opt-in. It puts the arm button into the map's control
 * stack — the same column the Layers and Measure buttons live in — and brings the
 * mode prompt, the drawing interaction and the section geometry with it. Maps that
 * do not mount it are untouched, which is why this is a mount rather than a prop on
 * the shared map.
 *
 * The drawer itself is the host page's to place, since only the page knows how it
 * wants to split the map against the panel below.
 *
 * @return {JSX.Element} The section geometry for this map.
 */
export const CrossSectionMapTool = (): JSX.Element => {
  const register = useSetAtom(registerCrossSectionToolAtom);
  const armed = useAtomValue(crossSectionArmedAtom);
  const { current: mapRef } = useMap();

  // The arm button is contributed to the map's control stack rather than imported by
  // it: the map offers a slot, the feature fills it, and the map is left knowing
  // nothing about cross-sections. Mounted here, so it appears on exactly the maps
  // that opted in and goes with them.
  useMapControl({
    id: 'cross-section',
    render: ({ className, closePanels }) => (
      <CrossSectionArmButton className={className} onArm={closePanels} />
    ),
  });

  useEffect(() => {
    register(true);
    return () => register(false);
  }, [register]);

  // Arming mounts the rail beside the map and disarming removes it, so the container
  // changes width both ways — and Mapbox only remeasures when told. Without this,
  // closing leaves the canvas at its old width with a blank strip where the rail was.
  useEffect(() => {
    const timer = setTimeout(() => mapRef?.resize(), 60);
    return () => clearTimeout(timer);
  }, [armed, mapRef]);

  return <CrossSectionMapLayer />;
};
